/**
 * FND-54: keeping a newly created event in view.
 *
 * Staff create events through `EventForm`, which defaults to `status: 'draft'`
 * so nothing reaches the membership by accident. Every list in the app,
 * however, asks for PUBLISHED events: Events.jsx defaults its staff filter to
 * 'published', and the calendar and dashboard send no status at all, which
 * `GET /events` resolves to 'published' server-side
 * (src/services/events/index.js handleListEvents). A freshly created draft
 * therefore landed outside every view the creator could see — the toast said
 * "Created", the list reloaded, and the event was nowhere. That is the
 * operator-reported "I created an event but it wasn't there the next day";
 * the record saved correctly every time, it was simply never rendered.
 *
 * These helpers keep the create flow honest: follow the event into whichever
 * bucket it actually went to, and say plainly when it is not yet visible to
 * members.
 */

/**
 * The status filter that will actually show an event just created with
 * `createdStatus`. Returns `currentFilter` unchanged when the new event
 * already belongs to the visible bucket (or the API omitted a status), so the
 * caller can tell "switch the filter" from "just reload".
 */
export function filterFollowingCreate(currentFilter, createdStatus) {
  if (!createdStatus) return currentFilter;
  return createdStatus;
}

/**
 * Toast copy for a created event. A draft is the default and is invisible to
 * members, so it says so rather than reporting a bare success the page can't
 * back up with a visible row.
 */
export function createdEventNotice(name, status) {
  if (status === 'draft') {
    return `Created "${name}" as a draft — members can't see it until you publish it.`;
  }
  if (status === 'published') {
    return `Created "${name}" — it's live for members now.`;
  }
  return `Created "${name}" (${status}).`;
}
